<template>
  <d-container
    fluid
    :class="{
      'main-content-container': true,
      'px-4': !isOrderView && !isCustomerView,
      'px-0': isOrderView || isCustomerView
    }">
    <div class="page-header-with-filters">
      <!-- Page Header -->
      <d-row
        no-gutters
        :class="{
          'page-header': true,
          'py-4': !isOrderView && !isCustomerView,
          'mb-4 pb-3 border-bottom': isOrderView || isCustomerView
        }">
        <d-col
          col
          sm="6"
          class="text-center text-sm-left mb-4 mb-sm-0">
          <span
            v-if="!isOrderView && !isCustomerView"
            class="text-uppercase page-subtitle">
            {{ $t('customer.contact.customersContacts') }}
          </span>
          <h3 class="page-title">
            <template v-if="isOrderView || isCustomerView">{{ $t('customer.contact.customerContactsList') }}</template>
            <template v-else>{{ $t('customer.contact.customersContactsList') }}</template>
          </h3>
        </d-col>
        <d-col
          col
          sm="6"
          class="text-right text-sm-right d-flex justify-content-end align-items-end">
          <d-button
            theme="success"
            @click.prevent="addNewContact">
            <i class="material-icons">add</i>
            {{ $t('customer.contact.addNewCustomerContact') }}
          </d-button>
        </d-col>
      </d-row>

      <list-filters
        v-if="!isOrderView && !isCustomerView"
        :config="filtersConfig"
        :isListLoaded="allDataLoaded"
        :view-name="'contacts'"
        @init="handleFiltersInit"
        @search="handleFiltersSearch" />
    </div>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              v-if="!isOrderView && !isCustomerView"
              scope="col"
              class="border-0">{{ $t('ui.order') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('order.orderStatus') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 220px;">{{ $t('ui.comments') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 220px;">{{ $t('users.userName') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 40px">{{ $t('customer.contact.type') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 120px;">{{ $t('customer.contact.contactDate') }}</th>
            <th
              scope="col"
              class="border-0 text-center"
              style="width: 150px;">{{ $t('inquiries.inquiryStatus') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 150px;">{{ $t('customer.contact.customerResponse') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 150px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(contact, index) of contacts"
            :key="'contacts-' + index">
            <td
              v-if="!isOrderView && !isCustomerView"
              class="align-middle">
              <d-badge
                v-if="contact.contact_type === 'contact'"
                theme="success"
                class="mb-1">
                {{ $t('users.customer') }}
              </d-badge>
              <d-badge
                v-else-if="contact.contact_type === 'market'"
                theme="primary"
                class="mb-1">
                {{ $t('marketCustomers.marketCustomer') }}
              </d-badge>
              <d-badge
                v-else-if="contact.contact_type === 'potential'"
                theme="warning"
                class="mb-1">
                {{ $t('potentialCustomers.potentialCustomerView') }}
              </d-badge>

              <br>

              <template v-if="contact.order_id">
                <router-link :to="'/zamowienia/edycja/' + parseInt(contact.order_id, 10)">
                  <template v-if="contact.order_name">{{ contact.order_name }}</template>
                  <template v-else>{{ $t('ui.lack') }}</template>
                </router-link>
              </template>
              <template v-else>
                <template v-if="contact.order_name">{{ contact.order_name }}</template>
                <template v-else>{{ $t('ui.lack') }}</template>
              </template>
              <br>

              <small>
                {{ $t('users.customer') }}:
                <strong>{{ contact.customer_name }}</strong>
              </small>

              <template v-if="contact.contact_type === 'contact'">
                <ul class="customer-tags-list">
                  <li
                    v-for="(tag, tindex) of contact.tags"
                    :key="'customer-tag-' + index + '-' + tindex">
                    <d-badge :style="'background-color:' + tag.color + '; color: ' + contrastColor(tag.color) + ';'">
                      {{ tag.name }}
                    </d-badge>
                  </li>
                </ul>
              </template>

              <div v-if="contact.contact_type === 'market' && contact.tag">
                <small>
                  {{ $t('ui.tags') }}:
                  <span class="text-primary">
                    {{ contact.tag }}
                  </span>
                </small>
              </div>
            </td>
            <td class="align-middle">
              <d-badge
                v-if="contact.order_status"
                :theme="getStatusClass(contact.order_status)"
                :outline="getOutline(contact.order_status)">
                {{ getStatusName(contact.order_status) }}
              </d-badge>
              <small v-else>&mdash;</small>
            </td>
            <td class="align-middle">
              <small v-if="contact.description">{{ contact.description }}</small>
              <small v-else>&mdash;</small>
            </td>
            <td class="align-middle">
              {{ contact.user_name }}
            </td>
            <td class="align-middle text-center">
              <template v-for="(type, index) of contact.types">
                <i
                  v-if="type === 'EMAIL'"
                  :key="'contact-type-icon-1-' + index"
                  class="material-icons cursor-help"
                  :title="$t('customer.contact.mailContact')">email</i>
                <i
                  v-else-if="type === 'PHONE'"
                  :key="'contact-type-icon-2-' + index"
                  class="material-icons cursor-help"
                  :title="$t('customer.contact.phoneContact')">call</i>
                <i
                  v-else-if="type === 'MEETING'"
                  :key="'contact-type-icon-3-' + index"
                  class="material-icons cursor-help"
                  :title="$t('customer.contact.typeMeeting')">groups</i>
                <i
                  v-else-if="type === 'PATTERN_SENDING'"
                  :key="'contact-type-icon-4-' + index"
                  class="material-icons cursor-help"
                  :title="$t('customer.contact.typePatternSending')">texture</i>
                <i
                  v-else-if="type === 'SPEC_CAT_SENDING'"
                  :key="'contact-type-icon-5-' + index"
                  class="material-icons cursor-help"
                  :title="$t('customer.contact.typeSpecCatSending')">unarchive</i>
                <img
                  v-else-if="availableTypes.indexOf(type) > -1 && typeIcons[type]"
                  :key="'contact-type-icon-6-' + index"
                  :src="typeIcons[type]"
                  height="24"
                  width="24"
                  :alt="typeNames[type]"
                  :title="typeNames[type]">
                <i
                  v-else-if="availableTypes.indexOf(type) > -1"
                  :key="'contact-type-icon-7-' + index"
                  class="material-icons cursor-help"
                  :title="typeNames[type]">help</i>
              </template>
            </td>
            <td class="align-middle">
              {{ contact.date }}
              <template v-if="contact.types.indexOf('MEETING') > -1 && contact.correspondence_city">
                <br>
                <small><i class="material-icons">location_on</i>{{ contact.correspondence_city }}</small>
              </template>
            </td>
            <td class="align-middle text-center">
              <template v-if="contact.communication && contact.communication.customer_status">
                <d-badge
                  :theme="contact.communication.customer_status === 'ACCEPTED' ? 'success' : 'danger'"
                  :outline="true">
                  {{ $t(`complaints.statusNames.${contact.communication.customer_status.toLowerCase()}`) }}
                </d-badge>
                <br>
                <small>{{ prepareDateString(contact.communication.customer_answer_at) || '-' }}</small>
              </template>
              <template v-else>
                <small>&mdash;</small>
              </template>
            </td>
            <td class="align-middle">
              <small
                v-if="contact.communication && contact.communication.customer_description"
                class="text-truncate truncate-multiline">{{ contact.communication.customer_description }}</small>
              <small v-else>&mdash;</small>
            </td>
            <td
              class="align-middle"
              style="width: 60px;">
              <d-button
                theme="primary"
                @click.native.prevent="editContact(contact.id, contact.contact_type, contact)"
                :title="$t('ui.edit')"
                class="mr-2">
                <i class="material-icons">edit</i>
              </d-button>

              <d-button
                theme="danger"
                :disabled="removeInProgress || !userIsAllowedToDeleteContact()"
                @click.native.prevent="removeContact(contact.id, contact.contact_type)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="contacts.length === 0">
            <td
              :colspan="(!isOrderView && !isCustomerView) ? 9 : 8"
              class="text-center">
              {{ $t('customer.contact.noContactsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded && !loadError"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import FormUtils from './../utils/FormUtils.js';
import CalendarUtils from '@/utils/CalendarUtils.js';
import orderStatusClasses from './../data/order-status-classes.js';
import orderStatuses from './../data/order-statuses.js';
import Pagination from './../components/common/Pagination.vue';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'contacts',
  mixins: [
    HasPagination
  ],
  props: {
    isOrderView: {
      required: false,
      default: false
    },
    isCustomerView: {
      required: false,
      default: false
    },
    orderID: {
      required: false,
      default: false
    },
    customerID: {
      required: false,
      default: false
    }
  },
  components: {
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedWorkers && this.loadedTypes;
    },
    orderWorkerOptions () {
      return this.workers.map(worker => ({
        text: worker.name,
        value: worker.id
      }));
    },
    availableTypes () {
      return this.contactTypes.map(contactType => contactType.type);
    },
    typeOptions () {
      return this.contactTypes.map(contactType => ({
        text: contactType.name,
        value: contactType.type
      }));
    },
    typeIcons () {
      let icons = {};

      for (let i = 0; i < this.contactTypes.length; i++) {
        if (this.contactTypes[i].icon_url) {
          let type = this.contactTypes[i].type;
          icons[type] = this.contactTypes[i].icon_url;
        }
      }

      return icons;
    },
    typeNames () {
      let names = {};

      for (let i = 0; i < this.contactTypes.length; i++) {
        let type = this.contactTypes[i].type;
        names[type] = this.contactTypes[i].name;
      }

      return names;
    },
    orderStatusOptions () {
      return [
        {
          text: this.$t('order.newOffer'),
          value: 'NEW'
        },
        {
          text: this.$t('order.sentToCustomer'),
          value: 'SENT_TO_CUSTOMER'
        },
        {
          text: this.$t('order.offerClosed'),
          value: 'OFFER_CLOSED'
        },
        {
          text: this.$t('order.toCollect'),
          value: 'TO_COLLECT'
        },
        {
          text: this.$t('order.waitingPurchaseProducts'),
          value: 'WAITING_PURCHASE_PRODUCTS'
        },
        {
          text: this.$t('ui.order'),
          value: 'ORDER'
        },
        {
          text: this.$t('order.inProduction'),
          value: 'IN_PRODUCTION'
        },
        {
          text: this.$t('order.toCheckInWarehouse'),
          value: 'TO_CHECK'
        },
        {
          text: this.$t('order.readyForShipping'),
          value: 'PREPARATION_FOR_SHIPPING'
        },
        {
          text: this.$t('order.waitingForShipping'),
          value: 'WAITING_FOR_SHIPPING'
        },
        {
          text: this.$t('order.sent'),
          value: 'SENT'
        },
        {
          text: this.$t('order.pendingPayment'),
          value: 'PENDING_PAYMENT'
        },
        {
          text: this.$t('order.ended'),
          value: 'ENDED'
        },
        {
          text: this.$t('order.toDelete'),
          value: 'TO_DELETE'
        },
        {
          text: this.$t('order.deleted'),
          value: 'DELETED'
        }
      ];
    },
    contactTypeOptions () {
      return [
        {
          text: this.$t('users.customer'),
          value: 'contact'
        },
        {
          text: this.$t('marketCustomers.marketCustomer'),
          value: 'market'
        },
        {
          text: this.$t('potentialCustomers.potentialCustomerView'),
          value: 'potential'
        }
      ];
    },
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    },
    userID () {
      return parseInt(this.$store.getters.getUserID, 10);
    },
    filtersConfig () {
      return [
        {
          key: 'order_name',
          apiKey: 'order_name',
          label: this.$t('ui.orderName'),
          placeholder: this.$t('purchase.enterOrderName'),
          type: 'text',
          isBasic: true,
          defaultValue: '',
        },
        {
          key: 'date_range',
          label: this.$t('ui.chooseDate'),
          type: 'daterange',
          isBasic: true,
          defaultValue: { startDate: null, endDate: null },
          apiTransform: (val, where) => {
            if (val.startDate) {
              where['date_from'] = this.prepareDateString(val.startDate);
            }
            if (val.endDate) {
              where['date_to'] = this.prepareDateString(val.endDate);
            }
          }
        },
        {
          key: 'contact_type',
          apiKey: 'contact_type',
          label: this.$t('customer.contactGroupQuestionMark'),
          placeholder: this.$t('customer.contactGroupQuestionMark'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.contactTypeOptions,
        },
        // Advanced filters
        {
          key: 'customer_id',
          apiKey: 'customer_id',
          label: this.$t('users.customer'),
          type: 'customerSelect',
          isBasic: false,
          defaultValue: null,
          permission: () => !this.isCustomerView
        },
        {
          key: 'potential_customer_id',
          apiKey: 'potential_customer_id',
          label: this.$t('potentialCustomers.potentialCustomerView'),
          type: 'customerSelect',
          customerType: 'potential',
          isBasic: false,
          defaultValue: null,
          permission: () => !this.isCustomerView
        },
        {
          key: 'market_customer_id',
          apiKey: 'market_customer_id',
          label: this.$t('marketCustomers.marketCustomer'),
          type: 'customerSelect',
          customerType: 'market',
          isBasic: false,
          defaultValue: null,
          permission: () => !this.isCustomerView
        },
        {
          key: 'type',
          apiKey: 'type',
          label: this.$t('customer.contact.contactTypeQuestionMark'),
          placeholder: this.$t('customer.contact.contactTypeQuestionMark'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.typeOptions
        },
        {
          key: 'user_id',
          apiKey: 'user_id',
          label: this.$t('order.isWorker'),
          placeholder: this.$t('order.isWorker'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.orderWorkerOptions
        },
        {
          key: 'order_status',
          apiKey: 'order_status',
          label: this.$t('status.statusQuestionMark'),
          placeholder: this.$t('status.statusQuestionMark'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.orderStatusOptions
        }
      ];
    }
  },
  data () {
    return {
      contacts: [],
      contactTypes: [],
      workers: [],
      loaded: false,
      loadError: false,
      loadedTypes: false,
      loadedWorkers: false,
      removeInProgress: false,
      skipLSPagination: false,
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  created () {
    if (this.isOrderView) {
      this.skipLSPagination = true;
    }
  },
  mounted () {
    this.loadAdditionalData();
    this.$bus.$on('contacts-refresh-list', this.loadFilteredData);

    if (this.isOrderView || this.isCustomerView) {
      this.loadFilteredData();
    }
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = cloneDeep(this.apiQueryPayload) || {};

      if (this.isOrderView && this.orderID) {
        where.order_id = this.orderID;
      }

      if (this.isCustomerView && this.customerID) {
        where.customer_id = this.customerID;
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/all-contacts/items?page=' + this.currentPage,
        listField: 'contacts',
        params: {
          where
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/items',
        loadedKey: 'loadedWorkers',
        params: {
          return: 'get',
          where: {
            type: ['worker', 'admin']
          }
        },
        outputKey: 'workers',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-contact-types/items',
        loadedKey: 'loadedTypes',
        outputKey: 'contactTypes',
        errorKey: 'loadError'
      });
    },
    removeContact (contactID, contactType) {
      this.removeInProgress = true;
      let endpoint = '/contacts/delete/';

      if (contactType === 'potential') {
        endpoint = '/potential-customers/contacts/delete/';
      } else if (contactType === 'market') {
        endpoint = '/market-customers/contacts/delete/';
      }

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: endpoint,
        id: contactID,
        successTitle: this.$t('customer.contact.removeContactSuccessTitle'),
        successText: this.$t('customer.contact.removeContactSuccessText'),
        confirmTitle: this.$t('customer.contact.removeContactConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.contact.removeContactErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    editContact (contactID, contactType = false, contactData = false) {
      if (contactType === 'potential') {
        this.$router.push('/potencjalni-klienci/podglad/' + contactData.potential_customer_id);
      } else if (contactType === 'market') {
        this.$router.push('/klienci-z-targow/edycja/' + contactData.market_customer_id);
      } else if (this.isOrderView || this.isCustomerView) {
        this.$bus.$emit('show-order-contact-edit-popup', contactID);
      } else {
        this.$router.push('/kontakty-z-klientami/edycja/' + contactID);
      }
    },
    getStatusName (status) {
      return ListUtils.getKeyLabel(orderStatuses[this.$i18n.locale], status);
    },
    getStatusClass (status) {
      return ListUtils.getKeyLabel(orderStatusClasses, status).replace(' outline', '');
    },
    getOutline (status) {
      return ListUtils.getKeyLabel(orderStatusClasses, status).indexOf(' outline') > -1;
    },
    prepareDateString (date) {
      return CalendarUtils.formatDateForPHP(new Date(date));
    },
    userIsAllowedToDeleteContact () {
      if (this.userIsAdmin) {
        return true;
      }

      return false;
    },
    addNewContact () {
      if (this.isOrderView || this.isCustomerView) {
        this.$bus.$emit('show-order-contact-popup');
      } else {
        this.$router.push('/kontakty-z-klientami/nowy');
      }
    },
    contrastColor (colorBg) {
      let threshold = 130;
      let r = parseInt(colorBg.replace('#', '').substring(0, 2), 16);
      let g = parseInt(colorBg.replace('#', '').substring(2, 4), 16);
      let b = parseInt(colorBg.replace('#', '').substring(4, 6), 16);
      let brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;

      if (brightness > threshold) {
        return '#000';
      }

      return '#fff';
    }
  },
  beforeDestroy () {
    this.$bus.$off('contacts-refresh-list', this.loadFilteredData);
  }
};
</script>

<style lang="scss">
.cursor-help {
  cursor: help;
}

.truncate-multiline {
  display: -webkit-box !important;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
}

.customer-tags-list {
  list-style-type: none;
  padding: 0;
  margin: 5px 0;

  li {
    display: inline-block;
    font-size: 12px;
    margin-bottom: 3px;
    margin-right: 3px;
  }
}
</style>
